import * as PIXI from 'pixi.js';
import { createOptimizedVideoElement } from './videoTextureManager';
import useCanvasStore from '../stores/canvasStore';

// Helper function to create a placeholder sprite when texture loading fails
const createVideoPlaceholder = (componentData, elementId, x, y, viewport, dragStart, setHoveredElement, canvasElements, errorMessage) => {
  console.warn('Creating video placeholder sprite due to error:', errorMessage);

  // Create a graphics object as placeholder
  const placeholder = new PIXI.Graphics();

  // Draw a grey rectangle with error indicator
  const width = 400;
  const height = 300;

  // Grey background
  placeholder.rect(-width / 2, -height / 2, width, height);
  placeholder.fill(0x808080, 0.5);

  // Border
  placeholder.rect(-width / 2, -height / 2, width, height);
  placeholder.stroke({ width: 2, color: 0x666666 });

  // Draw an X to indicate error
  placeholder.moveTo(-width / 4, -height / 4);
  placeholder.lineTo(width / 4, height / 4);
  placeholder.moveTo(width / 4, -height / 4);
  placeholder.lineTo(-width / 4, height / 4);
  placeholder.stroke({ width: 3, color: 0x444444 });

  // Set position
  placeholder.x = x;
  placeholder.y = y;
  placeholder.eventMode = 'static';
  placeholder.cursor = 'default';

  // Scale to reasonable size
  const maxSize = viewport.worldHeight / 1.2;
  const scale = Math.min(maxSize / width, maxSize / height);
  placeholder.scale.set(scale);

  // Store references
  placeholder.componentData = componentData;
  placeholder.elementId = elementId;
  placeholder.isPlaceholder = true; // Flag to identify placeholder sprites
  placeholder.loadError = errorMessage; // Store error message

  // Create invisible interactive area
  const interactiveArea = new PIXI.Graphics();
  interactiveArea.rect(-width / 2, -height / 2, width, height);
  interactiveArea.fill(0x000000, 0);
  interactiveArea.x = x;
  interactiveArea.y = y;
  interactiveArea.eventMode = 'static';
  interactiveArea.cursor = 'default';
  interactiveArea.scale.set(scale);
  interactiveArea.elementId = placeholder.elementId;

  placeholder.interactiveArea = interactiveArea;

  // Add drag functionality
  const handlePointerDown = (event) => {
    let targetSprite = placeholder;

    if (event.currentTarget === interactiveArea && interactiveArea.elementId) {
      const canvasElement = useCanvasStore.getState().canvasElements.find(el => el.id === interactiveArea.elementId);
      if (canvasElement && canvasElement.pixiSprite) {
        targetSprite = canvasElement.pixiSprite;
      }
    }

    if (!targetSprite || targetSprite.destroyed || targetSprite.scale === null) {
      console.warn('Invalid or destroyed sprite reference in handlePointerDown');
      return;
    }

    if (!targetSprite.parent) {
      console.warn('Sprite is no longer in the scene');
      return;
    }

    dragStart(targetSprite, event);
  };

  placeholder.on('pointerdown', handlePointerDown);
  interactiveArea.on('pointerdown', handlePointerDown);

  // Add hover handlers
  const handlePointerOver = () => {
    if (placeholder && placeholder.parent && placeholder.elementId) {
      setHoveredElement(placeholder.elementId);
    }
  };

  const handlePointerOut = () => {
    setHoveredElement(null);
  };

  placeholder.on('pointerover', handlePointerOver);
  placeholder.on('pointerout', handlePointerOut);
  interactiveArea.on('pointerover', handlePointerOver);
  interactiveArea.on('pointerout', handlePointerOut);

  // Add empty cleanup function
  placeholder._cleanupVideo = () => { };

  return placeholder;
};

export const pixiVideoSprite = async (
  componentData,
  videoUrl,
  viewport,
  dragStart,
  setHoveredElement,
  canvasElements,
  elementId,
  // Optional position parameters
  x = 0,
  y = 0,
  // Optional videoTextureManager for proper texture updates
  videoTextureManager = null) => {
  try {
    // Check if URL is accessible before attempting to load (quick 404 check)
    // Skip this check for external URLs that may have CORS restrictions
    // PixiJS can still load them as video elements don't require CORS for playback
    const isExternalUrl = !videoUrl.startsWith(window.location.origin) &&
      !videoUrl.startsWith('/') &&
      !videoUrl.startsWith('./');

    if (!isExternalUrl) {
      try {
        const response = await fetch(videoUrl, { method: 'HEAD' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (fetchError) {
        console.error('Video URL not accessible:', fetchError);
        // Create placeholder immediately for 404 or network errors
        return createVideoPlaceholder(
          componentData,
          elementId,
          x,
          y,
          viewport,
          dragStart,
          setHoveredElement,
          canvasElements,
          `Video not accessible: ${fetchError.message}`
        );
      }
    } else {
      console.log('Skipping HEAD check for external URL (CORS):');
    }

    if (PIXI.Assets.cache.has(videoUrl)) {
      const existingAsset = PIXI.Assets.cache.get(videoUrl);
      PIXI.Assets.cache.remove(videoUrl);
      // Optionally destroy the existing texture
      existingAsset.destroy();
    }

    // Load texture with timeout (60 seconds for large videos on slow networks)
    const LOAD_TIMEOUT = 60000;
    const texture = await Promise.race([
      PIXI.Assets.load(videoUrl),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Video load timeout - file may be too large or network is slow')), LOAD_TIMEOUT)
      )
    ]);

    let videoElement = null;

    // Set alpha mode to prevent premultiplication artifacts
    if (texture.source) {
      texture.source.alphaMode = "no-premultiply-alpha";

      // CRITICAL: Enable auto-update for video textures
      // This tells PixiJS to automatically update the texture every frame when the video is playing
      if ('autoUpdate' in texture.source) {
        texture.source.autoUpdate = true;
      }

      // Set update FPS to match our timeline (optional, PixiJS will use requestAnimationFrame)
      if ('updateFPS' in texture.source) {
        texture.source.updateFPS = 12; // 0 means update every frame (recommended for smooth playback)
      }

      // Get the actual video element from the texture source
      if (texture.source.resource) {
        // Replace our video element with the one from the texture
        videoElement = texture.source.resource;
        console.log('Got video element from texture:', videoElement);

        // Copy our settings to the PIXI video element
        videoElement.loop = false;
        videoElement.playsInline = true;
        videoElement.autoplay = false;

        // IMPORTANT: For online videos, we need to wait for metadata to load
        // before we can properly set audio properties
        const setupAudio = () => {
          // Default to unmuted with full volume for new videos
          videoElement.muted = false;
          videoElement.volume = 1.0;
          console.log('✅ Video audio initialized:', {
            muted: videoElement.muted,
            volume: videoElement.volume,
            hasAudio: videoElement.mozHasAudio || videoElement.webkitAudioDecodedByteCount > 0 || Boolean(videoElement.audioTracks?.length)
          });
        };

        // If metadata is already loaded, setup immediately
        if (videoElement.readyState >= 1) {
          setupAudio();
        } else {
          // Otherwise wait for metadata to load
          videoElement.addEventListener('loadedmetadata', setupAudio, { once: true });
        }

        // Pause it immediately to prevent auto-play
        videoElement.pause();
        videoElement.currentTime = 0;
      }
    }

    const sprite = new PIXI.Sprite(texture);

    // Register video with texture manager if provided
    if (videoTextureManager && videoElement) {
      videoTextureManager.registerVideo(elementId, sprite, videoElement);
    }

    // Store cleanup function on sprite
    sprite._cleanupVideo = () => {
      // Unregister from texture manager
      if (videoTextureManager) {
        videoTextureManager.unregisterVideo(elementId);
      }
      // Pause video on cleanup
      if (videoElement && !videoElement.paused) {
        videoElement.pause();
      }
    };

    // Set sprite properties
    sprite.x = x;
    sprite.y = y;
    sprite.anchor.set(0.5);
    sprite.eventMode = 'static';
    sprite.cursor = 'default'; // Changed from 'pointer' to 'default'

    // Calculate dimensions
    const spriteWidth = sprite.width;
    const spriteHeight = sprite.height;
    const spriteAnchorX = -spriteWidth / 2;
    const spriteAnchorY = -spriteHeight / 2;

    // Scale to reasonable size
    const maxSize = viewport.worldHeight / 1.2;
    let scale = 1;
    if (videoElement && videoElement.videoWidth && videoElement.videoHeight) {
      scale = Math.min(maxSize / videoElement.videoWidth, maxSize / videoElement.videoHeight);
    } else {
      // Use sprite dimensions as fallback
      scale = Math.min(maxSize / spriteWidth, maxSize / spriteHeight);
    }
    sprite.scale.set(scale);

    // Store references
    sprite.componentData = componentData;
    sprite.videoElement = videoElement;
    sprite.elementId = elementId;


    // Create invisible interactive area for extended hit area
    const interactiveArea = new PIXI.Graphics();


    interactiveArea.rect(spriteAnchorX, spriteAnchorY, spriteWidth, spriteHeight);
    interactiveArea.fill(0x000000, 0); // Transparent
    interactiveArea.x = x;
    interactiveArea.y = y;
    interactiveArea.eventMode = 'static';
    interactiveArea.cursor = 'default'; // Changed from 'pointer' to 'default'
    interactiveArea.scale.set(scale);
    // Store elementId on interactive area for reference
    interactiveArea.elementId = sprite.elementId;

    // Store reference to interactive area
    sprite.interactiveArea = interactiveArea;

    // Add drag and transform functionality
    const handlePointerDown = (event) => {
      // For interactive area events, we need to find the current sprite
      let targetSprite = sprite;

      // If the event is from the interactive area, find the sprite by elementId
      if (event.currentTarget === interactiveArea && interactiveArea.elementId) {
        const canvasElement = useCanvasStore.getState().canvasElements.find(el => el.id === interactiveArea.elementId);
        if (canvasElement && canvasElement.pixiSprite) {
          targetSprite = canvasElement.pixiSprite;
        }
      }

      // Check if sprite is still valid and not destroyed
      if (!targetSprite || targetSprite.destroyed || targetSprite.scale === null) {
        console.warn('Invalid or destroyed sprite reference in handlePointerDown');
        return;
      }

      // Also check if the sprite is still in the scene
      if (!targetSprite.parent) {
        console.warn('Sprite is no longer in the scene');
        return;
      }

      // Start drag with event for offset calculation (this will select the updated element)
      dragStart(targetSprite, event);

      // Transform controls will be shown by the useEffect when selectedElement changes
    };

    sprite.on('pointerdown', handlePointerDown);
    interactiveArea.on('pointerdown', handlePointerDown);

    // Add hover handlers
    const handlePointerOver = () => {
      // Check if sprite is still valid before setting hover
      if (sprite && sprite.parent && sprite.elementId) {
        setHoveredElement(sprite.elementId);
      }
    };

    const handlePointerOut = () => {
      setHoveredElement(null);
    };

    sprite.on('pointerover', handlePointerOver);
    sprite.on('pointerout', handlePointerOut);
    interactiveArea.on('pointerover', handlePointerOver);
    interactiveArea.on('pointerout', handlePointerOut);

    return sprite;
  } catch (error) {
    console.error('Error creating video sprite:', error);
    // Re-throw the error so it can be caught by the caller and update the loading indicator
    throw error;
  }
}